import { Box, Checkbox, Group, Modal, Stack } from '@mantine/core';
import { Prism } from '@mantine/prism';
import { useQueryClient } from '@tanstack/react-query';

import { showNotification } from '@mantine/notifications';
import {
	useForceStaleLineage,
	useForceUnstaleLineage,
	useGetLineageDebugInfo,
} from '@repo/api-codegen';
import { Button, CopyButton, Text, TextInput } from '@repo/foundations';
import type { FlowActions } from '@repo/lineage/store/LineageFlowUIStore.types';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { EntityNodeData } from '../../../components/LineageGraph/types';
import { getLineageErrorMessage } from '../../../components/LineageGraph/utils';
import { InspectLineageGraph } from './InspectLineageGraph';

function InspectEntityLineagePage() {
	const [entityId, setEntityId] = useState<string | null>(null);
	const [includeArchived, setIncludeArchived] = useState(false);
	const [debugModalOpened, setDebugModalOpened] = useState(false);
	const [selectedLineageId, setSelectedLineageId] = useState<string | null>(
		null
	);
	const entityIdRef = useRef<HTMLInputElement>(null);
	const queryClient = useQueryClient();

	const handleFetchLineage = () => {
		const entityIdValue = entityIdRef.current?.value;
		if (entityIdValue) {
			setEntityId(entityIdValue);
		}
	};

	const flowActions: FlowActions = useMemo(
		() => ({
			exportToCSV: {
				label: 'Export to CSV',
				icon: 'csv',
				onClick: () => {},
			},
		}),
		[]
	);

	// API mutation hooks
	const forceStaleMutation = useForceStaleLineage({
		onSuccess: () => {
			showNotification({
				title: 'Success',
				message: 'Entity marked as stale',
				color: 'green',
			});
			// Invalidate the lineage graph query to refresh the data
			queryClient.invalidateQueries({
				queryKey: ['admin', 'entity_lineage', 'lineage_graph'],
			});
		},
		onError: (error: any) => {
			showNotification({
				title: 'Error',
				message: `Failed to mark entity as stale: ${error.message}`,
				color: 'red',
			});
		},
	});

	const forceUnstaleMutation = useForceUnstaleLineage({
		onSuccess: () => {
			showNotification({
				title: 'Success',
				message: 'Entity marked as unstale',
				color: 'green',
			});
			// Invalidate the lineage graph query to refresh the data
			queryClient.invalidateQueries({
				queryKey: ['admin', 'entity_lineage', 'lineage_graph'],
			});
		},
		onError: (error: any) => {
			showNotification({
				title: 'Error',
				message: `Failed to mark entity as unstale: ${error.message}`,
				color: 'red',
			});
		},
	});

	const customMenuActions = useMemo(
		() => [
			{
				id: 'force-archive',
				icon: 'minus',
				label: 'Force archive lineage',
				condition: (nodeData: EntityNodeData) =>
					!(nodeData?.lineageArchived ?? false),
				onClick: (nodeData: EntityNodeData) => {
					forceStaleMutation.mutate({
						pathParams: {
							lineageId: nodeData?.ids?.lineage || '',
						},
					});
				},
			},
			{
				id: 'force-unarchive',
				icon: 'plus',
				label: 'Force Unarchive lineage',
				condition: (nodeData: EntityNodeData) =>
					nodeData?.lineageArchived ?? false,
				onClick: (nodeData: EntityNodeData) => {
					forceUnstaleMutation.mutate({
						pathParams: {
							lineageId: nodeData?.ids?.lineage || '',
						},
					});
				},
			},
		],
		[forceStaleMutation, forceUnstaleMutation]
	);

	// Debug info query
	const {
		data: debugInfo,
		isLoading: debugInfoLoading,
		error: debugInfoError,
	} = useGetLineageDebugInfo(
		{
			pathParams: { lineageId: selectedLineageId || '' },
		},
		{
			enabled: !!selectedLineageId && debugModalOpened,
		}
	);

	// Custom icon buttons with mutation handlers
	const customIconButtons = useMemo(
		() => [
			// Debug info icon for lineage nodes
			{
				id: 'debug-info',
				icon: 'pencil',
				tooltip: 'Debug Info',
				condition: (nodeData: EntityNodeData) => !nodeData.isRoot, // debug info is not available for root nodes since it is from the lineage between two entities
				onClick: (nodeData: EntityNodeData) => {
					setSelectedLineageId(nodeData?.ids?.lineage || '');
					setDebugModalOpened(true);
				},
			},
		],
		[]
	);

	const handleLineageApiError = useCallback((error: string) => {
		showNotification({
			title: 'Error',
			message: getLineageErrorMessage(error),
			color: 'red',
		});
	}, []);

	const handleDebugModalClose = useCallback(() => {
		setDebugModalOpened(false);
		setSelectedLineageId(null);
	}, []);

	const renderDebugModalContent = () => {
		if (debugInfoLoading) {
			return <Text>Loading debug information...</Text>;
		}

		if (debugInfoError) {
			return (
				<Box>
					<Text size="sm" color="text/critical/default" mb="xs">
						Error loading debug information:
					</Text>
					<Text size="sm" color="text/critical/default">
						{debugInfoError.detail || 'Unknown error occurred'}
					</Text>
				</Box>
			);
		}

		if (debugInfo) {
			// Check if stack_trace exists in lineage_debug_info
			const stackTrace = (debugInfo as any)?.lineage_debug_info?.stack_trace;

			// Create a copy of debugInfo to modify for display
			const displayDebugInfo = JSON.parse(JSON.stringify(debugInfo));

			// If stack_trace exists, format it for display
			if (stackTrace && typeof stackTrace === 'string') {
				// Replace the stack_trace with a formatted version for display
				displayDebugInfo.lineage_debug_info.stack_trace = stackTrace.replace(
					/\\n/g,
					'\n'
				);
			}

			return (
				<Box>
					<Group position="apart" mb="xs">
						<Text size="sm" weight="semibold">
							Lineage ID: {selectedLineageId}
						</Text>
						<CopyButton
							value={JSON.stringify(debugInfo, null, 2)}
							timeout={2000}
						/>
					</Group>

					{/* Full debug info as JSON */}
					<Text size="sm" weight="semibold" mb="xs">
						Full Debug Information:
					</Text>
					<Prism
						language="json"
						colorScheme="dark"
						style={{ maxHeight: '400px', overflow: 'auto' }}
					>
						{JSON.stringify(displayDebugInfo, null, 2)}
					</Prism>

					{/* Special rendering for stack_trace if it exists */}
					{stackTrace && typeof stackTrace === 'string' && (
						<Box mb="md">
							<Text size="sm" weight="semibold" mb="xs">
								Parsed Stack Trace:
							</Text>
							<Prism
								language="bash"
								colorScheme="dark"
								style={{ maxHeight: '200px', overflow: 'auto' }}
							>
								{stackTrace.replace(/\\n/g, '\n')}
							</Prism>
						</Box>
					)}
				</Box>
			);
		}

		return <Text>No debug information available</Text>;
	};

	return (
		<Stack spacing="md">
			<Stack spacing="md">
				<Text size="md">
					Warning: This page uses the django Lineage object to fetch
					stale/archived entities, instead of the SecodaEntityDocument Search
					that other lineage fetching uses. This could cause mis-matches.
				</Text>
				<TextInput
					ref={entityIdRef}
					label="Entity ID"
					placeholder="Enter entity ID"
				/>
				<Checkbox
					label="Include archived lineage"
					checked={includeArchived}
					onChange={(event) => setIncludeArchived(event.target.checked)}
				/>

				<Button type="submit" variant="primary" onClick={handleFetchLineage}>
					Fetch Lineage
				</Button>
			</Stack>

			{entityId && (
				<Box h="600px">
					<InspectLineageGraph
						id={entityId}
						includeArchived={includeArchived}
						onError={handleLineageApiError}
						flowActions={flowActions}
						customMenuActions={customMenuActions}
						customIconButtons={customIconButtons}
					/>
				</Box>
			)}

			{/* Debug Info Modal */}
			<Modal
				opened={debugModalOpened}
				onClose={handleDebugModalClose}
				title="Debug Information"
				size="lg"
			>
				<Stack spacing="md">{renderDebugModalContent()}</Stack>
			</Modal>
		</Stack>
	);
}

export default InspectEntityLineagePage;
